import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  KEY_DATES_TYPE,
  KeyDate,
  KeyDateSchedulerPayload,
  KeyDateToResolve,
  RelativePayload,
} from './key-dates-type.const';

import { throwError } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { issuePlusTenorEqualsMaturity } from 'src/app/form-config/validators/cross-field-validators/issue-plus-tenor-equals-maturity';
import { KeyDateDetail } from 'src/app/product-capture/model/classes/key-date-detail';
import { FREQUENCY } from 'src/app/product-capture/model/consts/frequency.const';
import { KEY_DATES } from 'src/app/product-capture/model/consts/key-dates.const';
import { OFFSET } from 'src/app/product-capture/model/consts/offset.const';
import { ROLL_CONVENTION } from 'src/app/product-capture/model/consts/roll-convention.const';
import { SETTLEMENT_TRADING } from 'src/app/product-capture/model/consts/settlement-trading.const';
import {
  PayoffEnum,
  ProductCaptureGroupsEnum,
  ProductTypeEnum,
} from 'src/app/product-capture/model/enums/product-capture.enums';
import { KeyDatesSnapshot } from 'src/app/product-capture/model/interfaces/key-dates-snapshot.interface';
import { ProductCaptureService } from 'src/app/product-capture/services/product-capture.service';
import { GetDateObjectToFullIsoString } from 'src/app/product-capture/utils/get-data-object-to-full-ISO-string';
import { GetDateObjectFromString } from 'src/app/product-capture/utils/get-date-object-from-string';
import { GetErrorMessageFromResponse } from 'src/app/product-capture/utils/get-error-message-from-response';
import { DcAlertInnerService } from 'src/app/ui-kit/dc-alert-inner/dc-alert-inner.service';
import { KeyDatesSnapshotService } from '../key-dates-snapshot.service';

@Component({
  selector: 'app-key-dates-modal',
  templateUrl: './key-dates-modal.component.html',
  styleUrls: ['./key-dates-modal.component.scss'],
})
export class KeyDatesModalComponent implements OnInit {
  keyDatesForm: UntypedFormGroup;

  productType: string;
  hideTradingDropdowns: boolean;

  rowData = [...KEY_DATES_TYPE]; // Data for building form rows
  keyDates = KEY_DATES;
  frequencyItems = FREQUENCY;
  tradingCalendarItemsUse: KeyDate[];
  settlementCalendarsItemsUse: KeyDate[];
  calendarItems = SETTLEMENT_TRADING;
  rollConventionItems = ROLL_CONVENTION;
  offsetItems = OFFSET;
  tenorField: string;
  frequencyField: string;
  tradingCalendars: string[];
  settlementCalendars: string[];
  settlementRollConvention: string;
  accrualRollConvention: string;
  tradingRollConvention: string;

  issueDate = KeyDateDetail.getEmpty();
  redemptionDate = KeyDateDetail.getEmpty();
  initialObservationDate = KeyDateDetail.getEmpty();
  finalObservationDate = KeyDateDetail.getEmpty();
  tradeDate = KeyDateDetail.getEmpty();
  productFinalisationDate = KeyDateDetail.getEmpty();
  interestStartDate = KeyDateDetail.getEmpty();
  interestEndDate = KeyDateDetail.getEmpty();

  productForm: FormGroup;

  refreshing = false;

  constructor(
    public modal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private productCaptureService: ProductCaptureService,
    private dcAlertInnerService: DcAlertInnerService,
    private keyDatesSnapshotService: KeyDatesSnapshotService
  ) {}

  ngOnInit(): void {
    this.keyDatesForm = this.fb.group(
      {
        kdTenor: [this.tenorField],
        kdFrequency: [this.frequencyField],
        settlementCalendars: [null],
        tradingCalendars: [this.tradingCalendars],
        tradingRollConvention: [this.tradingRollConvention],
        settlementRollConvention: [this.settlementRollConvention],
        accrualRollConvention: [this.accrualRollConvention],

        initialObservationDate: this.fb.group({
          initialObsOverride: [
            this.overrideSetterInput(this.initialObservationDate.priority),
          ],
          initialObsDate: [null],
          initialObsFrom: [this.initialObservationDate.anchorDate],
          initialObsOffset: [this.initialObservationDate.offset],
          initialObsCalendar: [this.initialObservationDate.calendar],
          initialObsRollConvention: [
            this.initialObservationDate.rollConvention,
          ],
        }),

        issueDate: this.fb.group({
          issueDateOverride: [
            this.overrideSetterInput(this.issueDate.priority),
          ],
          issueDateDate: [null],
          issueDateFrom: [this.issueDate.anchorDate],
          issueDateOffset: [this.issueDate.offset],
          issueDateCalendar: [this.issueDate.calendar],
          issueDateRollConvention: [this.issueDate.rollConvention],
        }),

        interestStartDate: this.fb.group({
          interestStartDateOverride: [
            this.overrideSetterInput(this.interestStartDate?.priority),
          ],
          interestStartDateDate: [this.interestStartDate?.date],
          interestStartDateFrom: [this.interestStartDate?.anchorDate],
          interestStartDateOffset: [this.interestStartDate?.offset],
          interestStartDateCalendar: [this.interestStartDate?.calendar],
          interestStartDateRollConvention: [
            this.interestStartDate?.rollConvention,
          ],
        }),

        interestEndDate: this.fb.group({
          interestEndDateOverride: [
            this.overrideSetterInput(this.interestEndDate?.priority),
          ],
          interestEndDateDate: [this.interestEndDate?.date],
          interestEndDateFrom: [this.interestEndDate?.anchorDate],
          interestEndDateOffset: [this.interestEndDate?.offset],
          interestEndDateCalendar: [this.interestEndDate?.calendar],
          interestEndDateRollConvention: [this.interestEndDate?.rollConvention],
        }),

        finalObservationDate: this.fb.group({
          finalObsOverride: [
            this.overrideSetterInput(this.finalObservationDate.priority),
          ],
          finalObsDate: [null],
          finalObsFrom: [this.finalObservationDate.anchorDate],
          finalObsOffset: [this.finalObservationDate.offset],
          finalObsCalendar: [this.finalObservationDate.calendar],
          finalObsRollConvention: [this.finalObservationDate.rollConvention],
        }),

        redemptionDateGroup: this.fb.group({
          redemptionOverride: [
            this.overrideSetterInput(this.redemptionDate.priority),
          ],
          redemptionDate: [null],
          redemptionFrom: [this.redemptionDate.anchorDate],
          redemptionOffset: [this.redemptionDate.offset],
          redemptionCalendar: [this.redemptionDate.calendar],
          redemptionRollConvention: [this.redemptionDate.rollConvention],
        }),

        tradeDateGroup: this.fb.group({
          tradeDateOverride: [
            this.overrideSetterInput(this.tradeDate.priority),
          ],
          tradeDateDate: [null],
          tradeDateFrom: [this.tradeDate.anchorDate],
          tradeDateOffset: [this.tradeDate.offset],
          tradeDateCalendar: [this.tradeDate.calendar],
          tradeDateRollConvention: [this.tradeDate.rollConvention],
        }),
        productFinalisationDate: this.fb.group({
          productFinalisationDateOverride: [
            this.overrideSetterInput(this.productFinalisationDate.priority),
          ],
          productFinalisationDateDate: [null],
          productFinalisationDateFrom: [
            this.productFinalisationDate.anchorDate,
          ],
          productFinalisationDateOffset: [this.productFinalisationDate.offset],
          productFinalisationDateCalendar: [
            this.productFinalisationDate.calendar,
          ],
          productFinalisationDateRollConvention: [
            this.productFinalisationDate.rollConvention,
          ],
        }),
      },
      {
        validators: [
          issuePlusTenorEqualsMaturity(
            'issueDate',
            'kdTenor',
            'redemptionDate',
            false
          ),
        ],
      }
    );

    setTimeout(() => {
      this.updateDates();
      this.keyDatesForm.markAllAsTouched();
      this.keyDatesForm.updateValueAndValidity();
    }, 0);
  }

  ngAfterContentChecked(): void {
    this.cd.detectChanges();
  }

  updateDates(): void {
    this.setFormValue(this.tradingCalendars, 'tradingCalendars');
    this.setFormValue(this.settlementCalendars, 'settlementCalendars');
    this.setFormValue(
      this.settlementRollConvention,
      'settlementRollConvention'
    );

    this.setFormValue(this.tradingRollConvention, 'tradingRollConvention');
    this.setFormValue(this.accrualRollConvention, 'accrualRollConvention');

    this.setFormValue(
      this.initialObservationDate?.date,
      'initialObservationDate',
      'initialObsDate'
    );

    this.setFormValue(this.issueDate?.date, 'issueDate', 'issueDateDate');
    this.setFormValue(
      this.interestStartDate?.date,
      'interestStartDate',
      'interestStartDateDate'
    );
    this.setFormValue(
      this.interestEndDate?.date,
      'interestEndDate',
      'interestEndDateDate'
    );
    this.setFormValue(
      this.finalObservationDate?.date,
      'finalObservationDate',
      'finalObsDate'
    );
    this.setFormValue(
      this.redemptionDate?.date,
      'redemptionDateGroup',
      'redemptionDate'
    );
    this.setFormValue(this.tradeDate?.date, 'tradeDateGroup', 'tradeDateDate');
    this.setFormValue(
      this.productFinalisationDate?.date,
      'productFinalisationDate',
      'productFinalisationDateDate'
    );
  }

  // Modal props, save, cancel
  setDialogProps(props: any): void {
    this.tradingCalendarItemsUse = [
      { id: 'Please Select', displayValue: 'Please Select', disabled: true },
      ...props.tradingCalendarItemsUse,
    ];

    this.settlementCalendarsItemsUse = [
      { id: 'Please Select', displayValue: 'Please Select', disabled: true },
      ...props.settlementCalendarsItemsUse,
    ];

    this.productType = props.productType;

    this.productForm = props.productForm;

    this.tenorField = props.tenorField;
    this.frequencyField = props.frequencyField;
    this.tradingCalendars = props.tradingCalendars;
    this.settlementCalendars = props.settlementCalendars;

    this.issueDate = props.issueDate;
    this.interestStartDate = props.interestStartDate;
    this.interestEndDate = props.interestEndDate;
    this.redemptionDate = props.redemptionDate;
    this.initialObservationDate = props.initialObservationDate;
    this.finalObservationDate = props.finalObservationDate;
    this.tradeDate = props.tradeDate;
    this.productFinalisationDate = props.productFinalisationDate;

    this.settlementRollConvention = props.settlementRollConvention;
    this.accrualRollConvention = props.accrualRollConvention;
    this.tradingRollConvention = props.tradingRollConvention;

    this.hideTradingDropdowns = this.productType === ProductTypeEnum.CLN;

    if (!props.showProductFinalisationDateRow) {
      this.setHiddenValue(true, 'productFinalisationDate');
    } else {
      this.setHiddenValue(false, 'productFinalisationDate');
    }

    if (props.couponType === 'NONE') {
      this.setHiddenValue(true, 'interestStartDate');
      this.setHiddenValue(true, 'interestEndDate');
      if (this.keyDatesForm) {
        this.setFormValue(null, 'interestStartDate', 'interestStartDateDate');
        this.setFormValue(null, 'interestEndDate', 'interestEndDateDate');
      }
      this.interestStartDate = null;
      this.interestEndDate = null;
    } else {
      this.setHiddenValue(false, 'interestEndDate');
      this.setHiddenValue(false, 'interestStartDate');
    }
  }

  setHiddenValue(value: boolean, ...groupNames: string[]): void {
    this.rowData.forEach((row) => {
      if (groupNames.includes(row.groupName)) {
        row.hidden = value;
      }
    });
  }

  cancel(): void {
    this.modal.close('cancel');
  }

  save(options?: any): void {
    if (options?.recordSnapshot) {
      const snapshotComparisonResult =
        this.keyDatesSnapshotService.compareSnapshot(this.prepareSnapshot());

      if (snapshotComparisonResult?.isSnapshotObsolete) {
        this.dcAlertInnerService.showAlert({
          type: 'danger',
          message: snapshotComparisonResult.message,
        });
        return;
      }
    }

    const interestStartDate = this.getFormValue(
      'interestStartDate',
      'interestStartDateDate'
    );

    const interestEndDate = this.getFormValue(
      'interestEndDate',
      'interestEndDateDate'
    );
    const payload = {
      tenorField: this.getFormValue('kdTenor'),
      frequencyField: this.getFormValue('kdFrequency'),
      tradingCalendars: this.getFormValue('tradingCalendars'),
      settlementCalendars: this.getFormValue('settlementCalendars'),
      settlementRollConvention: this.getFormValue('settlementRollConvention'),
      accrualRollConvention: this.getFormValue('accrualRollConvention'),
      tradingRollConvention: this.getFormValue('tradingRollConvention'),
      issueDateField: this.getFormValue('issueDate', 'issueDateDate'),
      issueDatePriority: this.overrideSetterOutput(
        this.getFormValue('issueDate', 'issueDateOverride')
      ),
      issueDateAnchorDate: this.getFormValue('issueDate', 'issueDateFrom'),
      issueDateOffset: this.getFormValue('issueDate', 'issueDateOffset'),
      issueDateCalendar: this.getFormValue('issueDate', 'issueDateCalendar'),
      issueDateRollConvention: this.getFormValue(
        'issueDate',
        'issueDateRollConvention'
      ),

      ...(interestStartDate && {
        ...this.keyDatesForm.get('interestStartDate').value,
        interestStartDatePriority: this.overrideSetterOutput(
          this.getFormValue('interestStartDate', 'interestStartDateOverride')
        ),
      }),

      ...(interestEndDate && {
        ...this.keyDatesForm.get('interestEndDate').value,
        interestEndDatePriority: this.overrideSetterOutput(
          this.getFormValue('interestEndDate', 'interestEndDateOverride')
        ),
      }),

      redemptionDateField: this.getFormValue(
        'redemptionDateGroup',
        'redemptionDate'
      ),
      redemptionDatePriority: this.overrideSetterOutput(
        this.getFormValue('redemptionDateGroup', 'redemptionOverride')
      ),
      redemptionDateAnchorDate: this.getFormValue(
        'redemptionDateGroup',
        'redemptionFrom'
      ),
      redemptionDateOffset: this.getFormValue(
        'redemptionDateGroup',
        'redemptionOffset'
      ),
      redemptionDateCalendar: this.getFormValue(
        'redemptionDateGroup',
        'redemptionCalendar'
      ),
      redemptionDateRollConvention: this.getFormValue(
        'redemptionDateGroup',
        'redemptionRollConvention'
      ),

      initialObservationField: this.getFormValue(
        'initialObservationDate',
        'initialObsDate'
      ),
      initialObservationPriority: this.overrideSetterOutput(
        this.getFormValue('initialObservationDate', 'initialObsOverride')
      ),
      initialObservationAnchorDate: this.getFormValue(
        'initialObservationDate',
        'initialObsFrom'
      ),
      initialObservationOffset: this.getFormValue(
        'initialObservationDate',
        'initialObsOffset'
      ),
      initialObservationCalendar: this.getFormValue(
        'initialObservationDate',
        'initialObsCalendar'
      ),
      initialObservationRollConvention: this.getFormValue(
        'initialObservationDate',
        'initialObsRollConvention'
      ),

      finalObservationField: this.getFormValue(
        'finalObservationDate',
        'finalObsDate'
      ),
      finalObservationPriority: this.overrideSetterOutput(
        this.getFormValue('finalObservationDate', 'finalObsOverride')
      ),
      finalObservationAnchorDate: this.getFormValue(
        'finalObservationDate',
        'finalObsFrom'
      ),
      finalObservationOffset: this.getFormValue(
        'finalObservationDate',
        'finalObsOffset'
      ),
      finalObservationCalendar: this.getFormValue(
        'finalObservationDate',
        'finalObsCalendar'
      ),
      finalObservationRollConvention: this.getFormValue(
        'finalObservationDate',
        'finalObsRollConvention'
      ),

      tradeDateField: this.getFormValue('tradeDateGroup', 'tradeDateDate'),
      tradeDatePriority: this.overrideSetterOutput(
        this.getFormValue('tradeDateGroup', 'tradeDateOverride')
      ),
      tradeDateAnchorDate: this.getFormValue('tradeDateGroup', 'tradeDateFrom'),
      tradeDateOffset: this.getFormValue('tradeDateGroup', 'tradeDateOffset'),
      tradeDateCalendar: this.getFormValue(
        'tradeDateGroup',
        'tradeDateCalendar'
      ),
      tradeDateRollConvention: this.getFormValue(
        'tradeDateGroup',
        'tradeDateRollConvention'
      ),

      productFinalisationDateValue: this.getFormValue(
        'productFinalisationDate',
        'productFinalisationDateDate'
      ),
      productFinalisationDatePriority: this.overrideSetterOutput(
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateOverride'
        )
      ),
      productFinalisationDateAnchorDate: this.getFormValue(
        'productFinalisationDate',
        'productFinalisationDateFrom'
      ),
      productFinalisationDateOffset: this.getFormValue(
        'productFinalisationDate',
        'productFinalisationDateOffset'
      ),
      productFinalisationDateCalendar: this.getFormValue(
        'productFinalisationDate',
        'productFinalisationDateCalendar'
      ),
      productFinalisationDateRollConvention: this.getFormValue(
        'productFinalisationDate',
        'productFinalisationDateRollConvention'
      ),
    };

    this.modal?.close(payload);

    if (options?.recordSnapshot) {
      this.keyDatesSnapshotService.recordSnapshot(this.prepareSnapshot());
    }
  }

  overrideSetterInput(value: string | null): boolean {
    return value === 'absolute' ? true : false;
  }

  overrideSetterOutput(
    value: string | boolean | null
  ): 'absolute' | 'relative' {
    return value === true ? 'absolute' : 'relative';
  }

  refresh(): void {
    let payload = this.keyDatesPayloadWithRelativeFields();
    this.refreshing = true;

    this.productCaptureService
      .postToScheduleToResolveKeyDates(payload)
      .pipe(
        first(),
        catchError((error) => {
          console.error(error);
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: `Error: ${GetErrorMessageFromResponse(
              error,
              'Unexpected error while refreshing key dates'
            )}`,
          });

          this.refreshing = false;
          return throwError(() => error);
        })
      )
      .subscribe(
        ({
          finalFixingDate,
          initialFixingDate,
          issueDate,
          interestStartDate,
          interestEndDate,
          maturityDate,
          tradeDate,
        }) => {
          this.setFormValue(
            GetDateObjectFromString(finalFixingDate),
            'finalObservationDate',
            'finalObsDate'
          );
          this.setFormValue(
            GetDateObjectFromString(initialFixingDate),
            'initialObservationDate',
            'initialObsDate'
          );
          this.setFormValue(
            GetDateObjectFromString(issueDate),
            'issueDate',
            'issueDateDate'
          );
          this.setFormValue(
            GetDateObjectFromString(maturityDate),
            'redemptionDateGroup',
            'redemptionDate'
          );
          this.setFormValue(
            GetDateObjectFromString(tradeDate),
            'tradeDateGroup',
            'tradeDateDate'
          );
          this.setFormValue(
            GetDateObjectFromString(interestStartDate),
            'interestStartDate',
            'interestStartDateDate'
          );
          this.setFormValue(
            GetDateObjectFromString(interestEndDate),
            'interestEndDate',
            'interestEndDateDate'
          );

          this.refreshing = false;
          this.keyDatesSnapshotService.recordSnapshot(this.prepareSnapshot());
        }
      );
  }

  minimumDataToPassScheduler(): KeyDateSchedulerPayload {
    const data = {
      issueDate: this.getAbsoluteFields(
        'issueDate',
        'issueDateOverride',
        'issueDateDate'
      ),
      interestStartDate: this.getAbsoluteFields(
        'interestStartDate',
        'interestStartDateOverride',
        'interestStartDateDate'
      ),
      interestEndDate: this.getAbsoluteFields(
        'interestEndDate',
        'interestEndDateOverride',
        'interestEndDateDate'
      ),
      maturityDate: this.getAbsoluteFields(
        'redemptionDateGroup',
        'redemptionOverride',
        'redemptionDate'
      ),
      initialFixingDate: this.getAbsoluteFields(
        'initialObservationDate',
        'initialObsOverride',
        'initialObsDate'
      ),
      finalFixingDate: this.getAbsoluteFields(
        'finalObservationDate',
        'finalObsOverride',
        'finalObsDate'
      ),
      tradeDate: this.getAbsoluteFields(
        'tradeDateGroup',
        'tradeDateOverride',
        'tradeDateDate'
      ),
      productFinalisationDate: this.getAbsoluteFields(
        'productFinalisationDate',
        'productFinalisationDateOverride',
        'productFinalisationDateDate'
      ),
    };
    return data;
  }

  private getFormValue(...keys: string[]): any {
    let formControl: any = this.keyDatesForm;

    for (let key of keys) {
      formControl = formControl.get(key);
    }

    return formControl.value || null;
  }

  private setFormValue(value: any, ...keys: string[]) {
    let formControl: any = this.keyDatesForm;

    for (let key of keys) {
      formControl = formControl.get(key);
    }

    formControl.setValue(value);
  }

  tradingOrSettlementArrayFinder(
    input: 'SETTLEMENT' | 'TRADING' | null
  ): string[] | [] {
    if (input === 'SETTLEMENT') {
      return this.getFormValue('settlementCalendars');
    }
    if (input === 'TRADING') {
      return this.getFormValue('tradingCalendars');
    } else return [];
  }

  getAbsoluteFields(
    formField: string,
    overrideField: string,
    dateField: string
  ): KeyDateToResolve {
    return {
      priority: this.overrideSetterOutput(
        this.getFormValue(formField, overrideField)
      ),
      value: this.getFormValue(formField, overrideField)
        ? GetDateObjectToFullIsoString(this.getFormValue(formField, dateField))
        : '',
    };
  }

  getRelativeFields(
    formField: string,
    rollConventionField: string,
    anchorField: string,
    offsetField: string,
    calenderField: string
  ): RelativePayload {
    return {
      rollConvention: this.getFormValue(formField, rollConventionField),
      anchorDate: this.getFormValue(formField, anchorField),
      offset:
        this.getFormValue(formField, offsetField) === 'TENOR'
          ? this.getFormValue('kdTenor')
          : this.getFormValue(formField, offsetField),
      holidays: this.tradingOrSettlementArrayFinder(
        this.getFormValue(formField, calenderField)
      ),
      nonWeekDays: this.tradingOrSettlementArrayFinder(
        this.getFormValue(formField, calenderField)
      ),
    };
  }

  keyDatesPayloadWithRelativeFields(): KeyDateSchedulerPayload {
    let payload = this.minimumDataToPassScheduler();
    if (payload.issueDate.priority === 'relative') {
      payload.issueDate.relative = this.getRelativeFields(
        'issueDate',
        'issueDateRollConvention',
        'issueDateFrom',
        'issueDateOffset',
        'issueDateCalendar'
      );
    }
    if (payload.interestStartDate.priority === 'relative') {
      payload.interestStartDate.relative = this.getRelativeFields(
        'interestStartDate',
        'interestStartDateRollConvention',
        'interestStartDateFrom',
        'interestStartDateOffset',
        'interestStartDateCalendar'
      );
    }
    if (payload.interestEndDate.priority === 'relative') {
      payload.interestEndDate.relative = this.getRelativeFields(
        'interestEndDate',
        'interestEndDateRollConvention',
        'interestEndDateFrom',
        'interestEndDateOffset',
        'interestEndDateCalendar'
      );
    }
    if (payload.maturityDate.priority === 'relative') {
      payload.maturityDate.relative = this.getRelativeFields(
        'redemptionDateGroup',
        'redemptionRollConvention',
        'redemptionFrom',
        'redemptionOffset',
        'redemptionCalendar'
      );
    }
    if (payload.initialFixingDate.priority === 'relative') {
      payload.initialFixingDate.relative = this.getRelativeFields(
        'initialObservationDate',
        'initialObsRollConvention',
        'initialObsFrom',
        'initialObsOffset',
        'initialObsCalendar'
      );
    }
    if (payload.finalFixingDate.priority === 'relative') {
      payload.finalFixingDate.relative = this.getRelativeFields(
        'finalObservationDate',
        'finalObsRollConvention',
        'finalObsFrom',
        'finalObsOffset',
        'finalObsCalendar'
      );
    }
    if (payload.tradeDate.priority === 'relative') {
      payload.tradeDate.relative = this.getRelativeFields(
        'tradeDateGroup',
        'tradeDateRollConvention',
        'tradeDateFrom',
        'tradeDateOffset',
        'tradeDateCalendar'
      );
    }
    if (payload.productFinalisationDate.priority === 'relative') {
      payload.productFinalisationDate.relative = this.getRelativeFields(
        'productFinalisationDate',
        'productFinalisationDateRollConvention',
        'productFinalisationDateFrom',
        'productFinalisationDateOffset',
        'productFinalisationDateCalendar'
      );
    }

    payload = this.omitEmptyKeyDates(payload);

    return payload;
  }

  omitEmptyKeyDates(payload: KeyDateSchedulerPayload): KeyDateSchedulerPayload {
    Object.keys(payload).forEach((key) => {
      if (
        (payload[key].priority === 'absolute' && !payload[key].value) ||
        (payload[key].priority === 'relative' &&
          !payload[key].relative?.anchorDate &&
          !payload[key].relative?.offset &&
          !payload[key].relative?.rollConvention)
      ) {
        delete payload[key];
      }
    });
    return payload;
  }

  private prepareSnapshot(): KeyDatesSnapshot {
    return {
      tradingCalendars: this.getFormValue('tradingCalendars'),
      settlementCalendars: this.getFormValue('settlementCalendars'),
      tenor: this.getFormValue('kdTenor'),
      issueDate: new KeyDateDetail(
        this.getFormValue('issueDate', 'issueDateDate'),
        this.getFormValue('issueDate', 'issueDateOverride') === true
          ? 'absolute'
          : 'relative',
        this.getFormValue('issueDate', 'issueDateFrom'),
        this.getFormValue('issueDate', 'issueDateOffset'),
        this.getFormValue('issueDate', 'issueDateCalendar'),
        this.getFormValue('issueDate', 'issueDateRollConvention')
      ),
      interestStartDate: new KeyDateDetail(
        this.getFormValue('interestStartDate', 'interestStartDateDate'),
        this.getFormValue('interestStartDate', 'interestStartDateOverride') ===
        true
          ? 'absolute'
          : 'relative',
        this.getFormValue('interestStartDate', 'interestStartDateFrom'),
        this.getFormValue('interestStartDate', 'interestStartDateOffset'),
        this.getFormValue('interestStartDate', 'interestStartDateCalendar'),
        this.getFormValue(
          'interestStartDate',
          'interestStartDateRollConvention'
        )
      ),
      interestEndDate: new KeyDateDetail(
        this.getFormValue('interestEndDate', 'interestEndDateDate'),
        this.getFormValue('interestEndDate', 'interestEndDateOverride') === true
          ? 'absolute'
          : 'relative',
        this.getFormValue('interestEndDate', 'interestEndDateFrom'),
        this.getFormValue('interestEndDate', 'interestEndDateOffset'),
        this.getFormValue('interestEndDate', 'interestEndDateCalendar'),
        this.getFormValue('interestEndDate', 'interestEndDateRollConvention')
      ),
      redemptionDate: new KeyDateDetail(
        this.getFormValue('redemptionDateGroup', 'redemptionDate'),
        this.getFormValue('redemptionDateGroup', 'redemptionOverride') === true
          ? 'absolute'
          : 'relative',
        this.getFormValue('redemptionDateGroup', 'redemptionFrom'),
        this.getFormValue('redemptionDateGroup', 'redemptionOffset'),
        this.getFormValue('redemptionDateGroup', 'redemptionCalendar'),
        this.getFormValue('redemptionDateGroup', 'redemptionRollConvention')
      ),
      initialObservationDate: new KeyDateDetail(
        this.getFormValue('initialObservationDate', 'initialObsDate'),
        this.getFormValue('initialObservationDate', 'initialObsOverride') ===
        true
          ? 'absolute'
          : 'relative',
        this.getFormValue('initialObservationDate', 'initialObsFrom'),
        this.getFormValue('initialObservationDate', 'initialObsOffset'),
        this.getFormValue('initialObservationDate', 'initialObsCalendar'),
        this.getFormValue('initialObservationDate', 'initialObsRollConvention')
      ),
      finalObservationDate: new KeyDateDetail(
        this.getFormValue('finalObservationDate', 'finalObsDate'),
        this.getFormValue('finalObservationDate', 'finalObsOverride') === true
          ? 'absolute'
          : 'relative',
        this.getFormValue('finalObservationDate', 'finalObsFrom'),
        this.getFormValue('finalObservationDate', 'finalObsOffset'),
        this.getFormValue('finalObservationDate', 'finalObsCalendar'),
        this.getFormValue('finalObservationDate', 'finalObsRollConvention')
      ),
      tradeDate: new KeyDateDetail(
        this.getFormValue('tradeDateGroup', 'tradeDateDate'),
        this.getFormValue('tradeDateGroup', 'tradeDateOverride') === true
          ? 'absolute'
          : 'relative',
        this.getFormValue('tradeDateGroup', 'tradeDateFrom'),
        this.getFormValue('tradeDateGroup', 'tradeDateOffset'),
        this.getFormValue('tradeDateGroup', 'tradeDateCalendar'),
        this.getFormValue('tradeDateGroup', 'tradeDateRollConvention')
      ),
      productFinalisationDate: new KeyDateDetail(
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateDate'
        ),
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateOverride'
        ),
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateFrom'
        ),
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateOffset'
        ),
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateCalendar'
        ),
        this.getFormValue(
          'productFinalisationDate',
          'productFinalisationDateRollConvention'
        )
      ),
    };
  }

  get couponType(): string {
    return this.productForm
      ?.get(ProductCaptureGroupsEnum.payoff)
      ?.get(PayoffEnum.coupon)
      ?.get('couponType')?.value;
  }
}
